/**
 * Image Generator 
 * Main class that coordinates the image generation functionality
 */
class ImageGenerator {
  constructor(options = {}) {
    console.log('Initializing ImageGenerator...');
    
    // Element references
    this.inputs = {
      input1: document.querySelector('[data-input="input1"]')
    };
    this.input = this.inputs.input1;
    this.inputLabel = document.querySelector('[data-input-label]');
    this.inputFieldContainer = document.querySelector('[data-input-field-container]');
    this.generateButton = document.querySelector('[data-generate-button]');
    this.buttonText = document.querySelector('[data-button-text]');
    this.gallery = document.querySelector('[data-gallery]');
    this.loadingSpinner = document.querySelector('[data-loading]');
    this.toastContainer = document.querySelector('[data-toast]');
    this.btnLoader = document.querySelector('[data-btn-loader]');
    this.galleryHeader = document.querySelector('[data-gallery-header]');
    this.colorCardsContainer = document.querySelector('[data-color-cards-container]');
    this.colorCardsTitle = document.querySelector('[data-color-cards-title]');
    
    // Add gallery loading overlay reference
    this.galleryLoadingOverlay = document.querySelector('[data-gallery-loading]');
    
    // Setup sub-modules
    const imageGeneratorElement = document.querySelector('[data-image-generator]');
    this.productId = imageGeneratorElement?.dataset.productId;
    this.customerId = imageGeneratorElement?.dataset.customerId;
    
    // Read product type and normalize it
    const rawProductType = imageGeneratorElement?.dataset.productType;
    console.log('[ImageGenerator Constructor] Raw productType:', `"${rawProductType}"`);
    this.productType = (rawProductType || 'default').trim().toLowerCase();
    console.log('[ImageGenerator Constructor] Normalized productType:', `"${this.productType}"`);
    
    // State management - COMPLETELY SEPARATED ARRAYS
    this.generatedImages = []; // ONLY completed, finalized images
    this.pendingGenerations = []; // ONLY images currently being generated
    this.selectedImage = null;
    this.fingerprint = window.userFingerprint || null;
    this.imagesLoaded = false;
    this.loadingMoreImages = false;
    this.hasMoreImages = false;
    this.page = 1;
    this.customily_ids = JSON.parse(imageGeneratorElement?.dataset.customilyIds || '[]');
    this.lastLoadTime = 0;
    this.mobileScrollCooldown = 3000;
    this.isMobile = window.innerWidth < 768;
    this.preloadThreshold = 0.75;
    this.generationTimeout = null;
    this.initialLoadDelay = 500; 
    this.intersectionObserver = null; 

    // NEW: Product filter toggle properties
    this.productFilterToggle = null;
    this.productFilterOnlyThis = false;

    // Generation state tracking - ATOMIC LOCKS
    this.isGenerating = false;
    this.generatingImageId = null;
    this.generationLock = false; // Prevents race conditions

    // Process featured images from metacampo
    this.featuredImagesFromMetacampo = this.processFeaturedImagesMetacampo(imageGeneratorElement);

    // Expose instance globally for ColorCardManager access
    window.imageGeneratorInstance = this;

    // Initialize sub-modules FIRST (before using colorCardManager methods)
    this.initializeSubModules();
    
    // Initialize color card data AFTER sub-modules are ready
    this.initializeColorCardData(imageGeneratorElement);
    
    this.init();
  }

  processFeaturedImagesMetacampo(imageGeneratorElement) {
    try {
      const rawFeaturedImages = imageGeneratorElement?.dataset.featuredImages;
      console.log('[ImageGenerator] Processing featured images metacampo:', rawFeaturedImages);
      
      if (!rawFeaturedImages || rawFeaturedImages.trim() === '' || rawFeaturedImages.trim() === '[]') {
        console.log('[ImageGenerator] No featured images in metacampo');
        return [];
      }

      const parsedImages = JSON.parse(rawFeaturedImages);
      
      if (!Array.isArray(parsedImages)) {
        console.warn('[ImageGenerator] Featured images metacampo is not an array:', parsedImages);
        return [];
      }

      // Transform metacampo images to match API structure
      const processedImages = parsedImages.map((imageItem, index) => {
        // Handle both string URLs (legacy) and object format {url: "...", prompt: "..."}
        let imageUrl, imagePrompt;
        
        if (typeof imageItem === 'string' && imageItem.trim() !== '') {
          // Legacy format: direct URL string
          imageUrl = imageItem.trim();
          imagePrompt = null;
        } else if (typeof imageItem === 'object' && imageItem !== null && imageItem.url) {
          // New format: object with url and prompt properties
          imageUrl = imageItem.url.trim();
          imagePrompt = imageItem.prompt || null;
        } else {
          console.warn('[ImageGenerator] Invalid image item format:', imageItem);
          return null;
        }
        
        if (!imageUrl) {
          console.warn('[ImageGenerator] Empty image URL found:', imageItem);
          return null;
        }

        const processedImage = {
          id: `metacampo_${Date.now()}_${index}`,
          image_url: imageUrl,
          image_path: '',
          status: 'success',
          is_featured: true,
          from_metacampo: true // Flag to identify origin
        };
        
        // Add prompt if available
        if (imagePrompt) {
          processedImage.prompt = imagePrompt;
        }
        
        return processedImage;
      }).filter(Boolean);

      console.log('[ImageGenerator] Processed featured images from metacampo:', processedImages);
      return processedImages;
    } catch (error) {
      console.error('[ImageGenerator] Error processing featured images metacampo:', error);
      return [];
    }
  }

  initializeSubModules() {
    console.log('[ImageGenerator] Initializing sub-modules...');
    
    // Initialize API service
    this.apiService = new ImageApiService({
      customerId: this.customerId,
      productId: this.productId,
      fingerprint: this.fingerprint
    });
    
    // Initialize gallery renderer
    this.galleryRenderer = new GalleryRenderer({
      gallery: this.gallery,
      galleryHeader: this.galleryHeader,
      loadMoreCallback: this.loadMoreImages.bind(this)
    });
    
    // Initialize UI handlers with all necessary elements including galleryLoadingOverlay
    this.uiHandlers = new UIHandlers({
      toastContainer: this.toastContainer,
      generateButton: this.generateButton,
      buttonText: this.buttonText,
      loadingSpinner: this.loadingSpinner,
      btnLoader: this.btnLoader,
      input: this.input,
      productType: this.productType,
      galleryLoadingOverlay: this.galleryLoadingOverlay
    });
    
    // Initialize color card manager
    this.colorCardManager = new ColorCardManager({
      colorCardsContainer: this.colorCardsContainer,
      colorCardsTitle: this.colorCardsTitle,
      input: this.input,
      productType: this.productType,
      onCardSelect: (cardData) => {
        this.uiHandlers.updateButtonStatus();
      }
    });
    
    // Initialize Customily integration
    this.customilyIntegration = new CustomilyIntegration({
      customily_ids: this.customily_ids
    });
    
    // Initialize event handlers
    this.eventHandlers = new EventHandlers({
      imageGenerator: this,
      input: this.input,
      generateButton: this.generateButton,
      gallery: this.gallery,
      colorCardManager: this.colorCardManager,
      uiHandlers: this.uiHandlers
    });

    // Initialize localStorage manager
    this.localStorageManager = new LocalStorageManager(this);
  }

  initializeColorCardData(imageGeneratorElement) {
    let colorCardsFromMetafield = [];
    const rawMetafieldValue = imageGeneratorElement?.dataset.colorCardsMetafield;
    
    console.log('[ImageGenerator DEBUG] Processing color card metafield data...');
    
    if (rawMetafieldValue && typeof rawMetafieldValue === 'string' && rawMetafieldValue.trim() !== '' && rawMetafieldValue.trim() !== 'null') {
      try {
        let cleanedValue = rawMetafieldValue;
        
        // Clean up JSON string
        if (cleanedValue.startsWith('"') && cleanedValue.endsWith('"')) {
          const innerContent = cleanedValue.slice(1, -1);
          if ((innerContent.startsWith('{') && innerContent.endsWith('}')) || (innerContent.startsWith('[') && innerContent.endsWith(']'))) {
            cleanedValue = innerContent;
          }
        }
        
        if (cleanedValue.includes('\\"')) {
          cleanedValue = cleanedValue.replace(/\\"/g, '"');
        }
        
        const parsedData = JSON.parse(cleanedValue);
        
        if (Array.isArray(parsedData)) {
          colorCardsFromMetafield = this.colorCardManager.validateColorCardData(parsedData);
        } else if (typeof parsedData === 'object' && parsedData !== null) {
          // Handle nested structures
          if (parsedData.hasOwnProperty('colors') && Array.isArray(parsedData.colors)) {
            colorCardsFromMetafield = this.colorCardManager.validateColorCardData([parsedData]);
          } else if (parsedData.hasOwnProperty('cards') && Array.isArray(parsedData.cards)) {
            colorCardsFromMetafield = this.colorCardManager.validateColorCardData(parsedData.cards);
          }
        }
        
        this.colorCardData = colorCardsFromMetafield.length > 0 ? colorCardsFromMetafield : this.colorCardManager.getDefaultColorCards();
        
      } catch (e) {
        console.error('[ImageGenerator ERROR] Failed to process metafield data:', e);
        this.colorCardData = this.colorCardManager.getDefaultColorCards();
      }
    } else {
      this.colorCardData = this.colorCardManager.getDefaultColorCards();
    }
    
    console.log('[ImageGenerator DEBUG] Final colorCardData:', this.colorCardData);
  }

  initializeProductFilter() {
    try {
      this.productFilterToggle = document.querySelector('[data-product-filter-toggle]');
      
      if (this.productFilterToggle) {
        // Check if user is logged in before showing toggle
        if (!this.isUserLoggedIn()) {
          // Hide the entire toggle container for non-logged users
          const toggleContainer = this.productFilterToggle.closest('[data-product-filter-container]') || 
                                 this.productFilterToggle.closest('.letz-ai__product-filter-toggle');
          if (toggleContainer) {
            toggleContainer.classList.add('hidden');
          }
          console.log('[ImageGenerator] Product filter toggle hidden for non-logged user');
          return;
        }
        
        // Load saved preference from localStorage
        this.productFilterOnlyThis = this.localStorageManager.getProductFilterPreference();
        
        // Set toggle state
        this.productFilterToggle.checked = this.productFilterOnlyThis;
        
        // Add event listener
        this.productFilterToggle.addEventListener('change', (e) => {
          this.handleProductFilterToggle(e);
        });
        
        console.log('[ImageGenerator] Product filter initialized, onlyThisProduct:', this.productFilterOnlyThis);
      }
    } catch (error) {
      console.error('[ImageGenerator] Error initializing product filter:', error);
    }
  }

  async handleProductFilterToggle(event) {
    try {
      this.productFilterOnlyThis = event.target.checked;
      
      console.log('[ImageGenerator] Product filter toggled:', this.productFilterOnlyThis);
      
      // Save preference to localStorage
      this.localStorageManager.saveProductFilterPreference(this.productFilterOnlyThis);
      
      // Apply the filter by reloading images
      await this.applyProductFilter();
      
    } catch (error) {
      console.error('[ImageGenerator] Error handling product filter toggle:', error);
      this.uiHandlers.showToast(window.t('toasts.filter_apply_error'), 'error');
    }
  }

  /**
   * Process color card data to add translated display names
   */
  processColorCardData() {
    console.log('[ImageGenerator] Processing color card data to add translated display names...');
    
    if (!this.colorCardData || !Array.isArray(this.colorCardData)) {
      console.log('[ImageGenerator] No color card data to process');
      return;
    }
    
    // Add display_name to each card data based on translations
    this.colorCardData.forEach((cardData, index) => {
      if (cardData && cardData.name && typeof cardData.name === 'string') {
        // Check if we have a translation for this palette name
        const paletteTranslationKey = `palette_names.${cardData.name}`;
        let translatedName = null;
        
        // Try to get translation using window.t if available
        if (window.t && typeof window.t === 'function') {
          try {
            translatedName = window.t(paletteTranslationKey);
            // If translation returns the same key, it means no translation was found
            if (translatedName === paletteTranslationKey) {
              translatedName = null;
            }
          } catch (error) {
            console.warn(`[ImageGenerator] Error getting translation for ${paletteTranslationKey}:`, error);
          }
        }
        
        // Set display_name: prioritize original display_name from metafield, then translation, then name
        cardData.display_name = cardData.display_name || translatedName || cardData.name;
        
        
      }
    });
    
    console.log('[ImageGenerator] Finished processing color card data');
  }

  /**
   * Apply product filter by reloading gallery with filter - WITH DETAILED LOGS
   */
  async applyProductFilter() {
    const isChecked = this.productFilterToggle.checked;
    console.log('🔄 [FILTER DEBUG] ====== applyProductFilter START ======');
    console.log('🔄 [FILTER DEBUG] Product filter toggled:', isChecked);
    console.log('🔄 [FILTER DEBUG] Gallery element:', this.gallery);
    console.log('🔄 [FILTER DEBUG] Initial gallery state:', {
      offsetHeight: this.gallery.offsetHeight,
      clientHeight: this.gallery.clientHeight,
      scrollHeight: this.gallery.scrollHeight,
      className: this.gallery.className,
      hasEmptyClass: this.gallery.classList.contains('letz-ai__gallery--empty'),
      hasMaintainHeightClass: this.gallery.classList.contains('maintain-height'),
      childrenCount: this.gallery.children.length,
      style_minHeight: this.gallery.style.minHeight
    });
    
    try {
      // Preserve gallery height to prevent visual jumps
      const currentHeight = this.gallery.offsetHeight;
      console.log('🔄 [FILTER DEBUG] Current gallery height to preserve:', currentHeight);
      
      if (currentHeight > 0) {
        this.gallery.style.minHeight = `${currentHeight}px`;
        this.gallery.classList.add('maintain-height');
        console.log('🔄 [FILTER DEBUG] Height preserved and maintain-height class added');
        console.log('🔄 [FILTER DEBUG] Gallery state after height preservation:', {
          style_minHeight: this.gallery.style.minHeight,
          hasMaintainHeightClass: this.gallery.classList.contains('maintain-height'),
          className: this.gallery.className
        });
      } else {
        console.log('🔄 [FILTER DEBUG] Current height is 0 or invalid, skipping height preservation');
      }

      // Show loading overlay immediately
      console.log('🔄 [FILTER DEBUG] Showing gallery loading overlay...');
      this.uiHandlers.showGalleryLoading();
      
      // Clear current gallery content while maintaining height
      console.log('🔄 [FILTER DEBUG] Clearing gallery content (append: false)...');
      console.log('🚀 [RENDER CALL] From filter toggle - CLEARING gallery');
      this.galleryRenderer.renderGallery([], { append: false });
      console.log('🔄 [FILTER DEBUG] Gallery cleared, checking state after clear:', {
        childrenCount: this.gallery.children.length,
        hasEmptyClass: this.gallery.classList.contains('letz-ai__gallery--empty'),
        hasMaintainHeightClass: this.gallery.classList.contains('maintain-height'),
        offsetHeight: this.gallery.offsetHeight,
        style_minHeight: this.gallery.style.minHeight
      });
      
      // Load images based on filter state
      console.log('🔄 [FILTER DEBUG] Loading images with productOnly:', isChecked);
      if (isChecked) {
        // Load only product-specific images
        await this.loadInitialImages(true);
      } else {
        // Load all images
        await this.loadInitialImages(false);
      }
      
      console.log('🔄 [FILTER DEBUG] Images loaded, final gallery state before cleanup:', {
        childrenCount: this.gallery.children.length,
        hasEmptyClass: this.gallery.classList.contains('letz-ai__gallery--empty'),
        hasMaintainHeightClass: this.gallery.classList.contains('maintain-height'),
        offsetHeight: this.gallery.offsetHeight,
        style_minHeight: this.gallery.style.minHeight
      });
      
    } catch (error) {
      console.error('🔄 [FILTER DEBUG] Error applying product filter:', error);
      this.uiHandlers.showToast(window.t('toasts.filter_apply_error'), 'error');
    } finally {
      // Always hide loading and clean up height preservation
      console.log('🔄 [FILTER DEBUG] Hiding gallery loading...');
      this.uiHandlers.hideGalleryLoading();
      
      // Remove height preservation after content settles (increased delay)
      setTimeout(() => {
        console.log('🔄 [FILTER DEBUG] Timeout reached (300ms), cleaning up height preservation...');
        console.log('🔄 [FILTER DEBUG] Gallery state before cleanup:', {
          hasMaintainHeightClass: this.gallery.classList.contains('maintain-height'),
          style_minHeight: this.gallery.style.minHeight,
          offsetHeight: this.gallery.offsetHeight,
          hasEmptyClass: this.gallery.classList.contains('letz-ai__gallery--empty'),
          childrenCount: this.gallery.children.length
        });
        
        if (this.gallery && this.gallery.classList.contains('maintain-height')) {
          this.gallery.style.minHeight = '';
          this.gallery.classList.remove('maintain-height');
          console.log('🔄 [FILTER DEBUG] Height preservation removed successfully');
          console.log('🔄 [FILTER DEBUG] Gallery state after cleanup:', {
            hasMaintainHeightClass: this.gallery.classList.contains('maintain-height'),
            style_minHeight: this.gallery.style.minHeight,
            offsetHeight: this.gallery.offsetHeight,
            hasEmptyClass: this.gallery.classList.contains('letz-ai__gallery--empty'),
            childrenCount: this.gallery.children.length
          });
        } else {
          console.log('🔄 [FILTER DEBUG] No maintain-height class found during cleanup');
        }
      }, 300); // Increased from 100ms to 300ms
      
      console.log('🔄 [FILTER DEBUG] ====== applyProductFilter END ======');
    }
  }

  async init() {
    console.log('Initializing ImageGenerator components...');
    
    const lastGenerationAttempt = localStorage.getItem('lastGenerationAttempt');
    if (lastGenerationAttempt) {
      const timePassed = Date.now() - parseInt(lastGenerationAttempt);
      if (timePassed > 30000) { 
        this.uiHandlers.forceResetState();
      }
    }
    
    // Check if user returned from login FIRST (without restoring input yet)
    this.localStorageManager.checkForLoginReturn();
    
    // Initialize product filter toggle
    this.initializeProductFilter();
    
    await new Promise(resolve => setTimeout(resolve, this.initialLoadDelay));
    
    console.log('Loading images and rendering color cards...');
    
    // Process color card data to add translated display names
    this.processColorCardData();
    
    // Set color card data and initialize UI based on product type FIRST
    this.colorCardManager.setColorCardData(this.colorCardData);
    this.colorCardManager.initUIBasedOnProductType(); // Check product type first
    
    // Render cards if product type is 'color_card', 'image_card', or 'image_card_with_upload'
    if (this.productType === 'color_card' || this.productType === 'image_card' || this.productType === 'image_card_with_upload') {
      console.log(`[ImageGenerator] Rendering cards for productType: ${this.productType}`);
      this.colorCardManager.renderColorCards(); // Render cards for all card types
    } else {
      console.log(`[ImageGenerator] Skipping card rendering for productType: ${this.productType}`);
    }
    
    // Initialize and show image upload area only for image_card_with_upload products
    if (this.productType === 'image_card_with_upload') {
      console.log('[ImageGenerator] Initializing image upload area for image_card_with_upload product');
      this.uiHandlers.initializeIntegratedUpload();
      this.uiHandlers.showIntegratedDropArea();
      
      // Check for URL parameter image loading (only for image_card_with_upload)
      await this.handleUrlParameterImageLoad();
    } else {
      console.log(`[ImageGenerator] Skipping image upload initialization for productType: ${this.productType}`);
    }
    
    // Clear input and deselect cards
    this.clearInputAndDeselectCards();
    
    // Check if user is logged in before making API calls
    if (this.isUserLoggedIn()) {
      console.log('[ImageGenerator] User is logged in, loading images from API + metacampo');
      console.log('[ImageGenerator] Available metacampo images before load:', this.featuredImagesFromMetacampo);
      await this.loadImagesFromAPI(true); // Load from API + metacampo
    } else {
      console.log('[ImageGenerator] User is NOT logged in, showing only metacampo images');
      console.log('[ImageGenerator] Available metacampo images:', this.featuredImagesFromMetacampo.length);
      
      // For non-logged users, show only metacampo images - ADD ONLY TO GENERATED IMAGES
      this.generatedImages = [...this.featuredImagesFromMetacampo];
      this.imagesLoaded = true;
      
      if (this.generatedImages.length > 0) {
        console.log('🚀 [RENDER CALL] From non-logged user - RESET with metacampo images');
        this.galleryRenderer.renderGallery(this.generatedImages, { append: false });

        // Check if source parameter exists to avoid auto-selection conflicts
        const urlParams = new URLSearchParams(window.location.search);
        const hasSourceParameter = urlParams.has('source');

        // Definir primeira imagem como selecionada apenas se não houver source parameter
        if (!hasSourceParameter && !this.selectedImage && this.generatedImages.length > 0) {
          this.selectedImage = this.generatedImages[0];
          // Aplicar visualmente a seleção da primeira imagem
          if (this.selectedImage && this.selectedImage.image_url) {
            this.galleryRenderer.updateSelectedState(this.selectedImage.image_url);
          }
        } else if (hasSourceParameter) {
          console.log('[ImageGenerator] Source parameter detected, skipping auto-selection of first image');
        }
        if (this.galleryHeader) {
          this.galleryHeader.classList.remove('hidden');
        }
      } else {
        console.log('[ImageGenerator] No metacampo images available for non-logged user');
        console.log('🚀 [RENDER CALL] From non-logged user - EMPTY gallery');
        this.galleryRenderer.renderGallery([], { append: false });
      }
      
      // Apply empty gallery optimization
      this.updateGalleryEmptyState();
      
      // Hide loading since we're done
      setTimeout(() => {
        this.checkAndHideGlobalLoading();
      }, 200);
    }
    
    // Auto-load first image to Customily on product open
    // Check if source parameter exists to avoid conflicts
    const urlParams = new URLSearchParams(window.location.search);
    const hasSourceParameter = urlParams.has('source');
    
    if (!hasSourceParameter && this.generatedImages?.length > 0 && this.customily_ids.length > 0) {
      console.log('[ImageGenerator] Auto-loading first image to Customily on product open...');
      
      // Priority 1: Featured images (existing functionality)
      const featuredImages = this.generatedImages.filter(img => img?.is_featured === true);
      if (featuredImages.length > 0) {
        console.log('[ImageGenerator] Found featured images, loading them to Customily...');
        this.customilyIntegration.setFeaturedImagesOnLoad(featuredImages).catch(error => {
          console.error('Error setting featured images on Customily (non-blocking):', error);
        });
      } else {
        // Priority 2: First image in array (new functionality)
        const firstImage = this.generatedImages[0];
        console.log('[ImageGenerator] No featured images found, loading first image to Customily:', firstImage);
        this.customilyIntegration.setFirstImageOnLoad(firstImage).catch(error => {
          console.error('Error setting first image on Customily (non-blocking):', error);
        });
      }
    } else if (hasSourceParameter) {
      console.log('[ImageGenerator] Source parameter detected, skipping auto-load of existing images to avoid conflicts');
    } else {
      console.log('[ImageGenerator] No images available or Customily not configured, skipping auto-load');
    }
    
    this.eventHandlers.setupEventListeners();
    this.uiHandlers.updateButtonStatus();
    this.uiHandlers.startCountdownIfNeeded();
    this.setupInfiniteScroll();
    
    console.log('Initialization complete');
    
    // IMPROVED: Restore input text from localStorage after color card data is loaded
    this.localStorageManager.scheduleInputRestoration();
  }

  /**
   * Update gallery empty state class - WITH DETAILED LOGS
   */
  updateGalleryEmptyState() {
    console.log('📊 [EMPTY STATE DEBUG] ====== updateGalleryEmptyState START ======');
    
    if (!this.gallery || !this.galleryHeader) {
      console.log('📊 [EMPTY STATE DEBUG] Missing gallery or galleryHeader elements, exiting');
      console.log('📊 [EMPTY STATE DEBUG] Gallery:', this.gallery);
      console.log('📊 [EMPTY STATE DEBUG] GalleryHeader:', this.galleryHeader);
      return;
    }
    
    // Don't update empty state while maintaining height during toggle
    const hasMaintainHeightClass = this.gallery.classList.contains('maintain-height');
    console.log('📊 [EMPTY STATE DEBUG] Checking maintain-height protection...');
    console.log('📊 [EMPTY STATE DEBUG] Has maintain-height class:', hasMaintainHeightClass);
    
    if (hasMaintainHeightClass) {
      console.log('📊 [EMPTY STATE DEBUG] BLOCKED: Skipping empty state update - gallery height being maintained');
      return;
    }
    
    console.log('📊 [EMPTY STATE DEBUG] Proceed with empty state analysis...');
    console.log('📊 [EMPTY STATE DEBUG] Gallery children count:', this.gallery.children.length);
    console.log('📊 [EMPTY STATE DEBUG] Gallery children list:', Array.from(this.gallery.children).map((child, index) => ({
      index,
      tagName: child.tagName,
      className: child.className,
      id: child.id,
      hasScrollSentinel: child.hasAttribute('data-scroll-sentinel'),
      isPlaceholder: child.id?.startsWith('placeholder-'),
      innerHTML: child.innerHTML.substring(0, 100) + (child.innerHTML.length > 100 ? '...' : '')
    })));
    
    const hasImages = this.gallery.children.length > 0;
    const hasNonSentinelImages = Array.from(this.gallery.children).some(child => 
      !child.hasAttribute('data-scroll-sentinel') && 
      !child.id?.startsWith('placeholder-') &&
      (child.classList.contains('letz-ai__gallery') || child.classList.contains('letz-ai__item'))
    );
    
    console.log('📊 [EMPTY STATE DEBUG] Analysis results:', { 
      hasImages, 
      hasNonSentinelImages,
      totalChildren: this.gallery.children.length 
    });
    
    console.log('📊 [EMPTY STATE DEBUG] Current gallery state BEFORE changes:', {
      offsetHeight: this.gallery.offsetHeight,
      clientHeight: this.gallery.clientHeight,
      scrollHeight: this.gallery.scrollHeight,
      hasEmptyClass: this.gallery.classList.contains('letz-ai__gallery--empty'),
      hasMaintainHeightClass: this.gallery.classList.contains('maintain-height'),
      className: this.gallery.className,
      style_minHeight: this.gallery.style.minHeight
    });
    
    if (!hasNonSentinelImages) {
      console.log('📊 [EMPTY STATE DEBUG] DECISION: Gallery should be marked as EMPTY');
      console.log('📊 [EMPTY STATE DEBUG] Adding letz-ai__gallery--empty class and hiding gallery header...');
      this.gallery.classList.add('letz-ai__gallery--empty');
      this.galleryHeader.classList.add('hidden'); // Hide gallery header when no real images
      console.log('📊 [EMPTY STATE DEBUG] Gallery marked as empty and header hidden');
    } else {
      console.log('📊 [EMPTY STATE DEBUG] DECISION: Gallery should be marked as having CONTENT');
      console.log('📊 [EMPTY STATE DEBUG] Removing letz-ai__gallery--empty class and showing gallery header...');
      this.gallery.classList.remove('letz-ai__gallery--empty');
      this.galleryHeader.classList.remove('hidden'); // Show gallery header when there are real images
      console.log('📊 [EMPTY STATE DEBUG] Gallery marked as having content and header shown');
    }
    
    console.log('📊 [EMPTY STATE DEBUG] Final gallery state AFTER changes:', {
      offsetHeight: this.gallery.offsetHeight,
      clientHeight: this.gallery.clientHeight,
      scrollHeight: this.gallery.scrollHeight,
      hasEmptyClass: this.gallery.classList.contains('letz-ai__gallery--empty'),
      hasMaintainHeightClass: this.gallery.classList.contains('maintain-height'),
      className: this.gallery.className,
      style_minHeight: this.gallery.style.minHeight,
      headerHidden: this.galleryHeader.classList.contains('hidden')
    });
    
    console.log('📊 [EMPTY STATE DEBUG] ====== updateGalleryEmptyState END ======');
  }

  clearInputAndDeselectCards() {
    console.log('[ImageGenerator] Clearing input and deselecting any cards...');
    
    // Always clear input during initialization
    if (this.input) {
      this.input.value = '';
      console.log('[ImageGenerator] Input cleared');
    }
    
    // Clear selected color card state
    this.selectedColorCard = null;
    
    // Remove visual selection from any color cards
    if (this.colorCardsContainer) {
      const selectedCards = this.colorCardsContainer.querySelectorAll('.letz-ai__color-card.selected');
      selectedCards.forEach(card => {
        card.classList.remove('selected');
        console.log('[ImageGenerator] Removed selection from color card');
      });
    }
    
    // Clear color card manager state
    if (this.colorCardManager) {
      this.colorCardManager.clearInput();
    }
    
    console.log('[ImageGenerator] Input and card selection cleared successfully');
  }

  setupEventListeners() {
    if (this.generateButton) {
      this.generateButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleGenerate();
        return false;
      });
    }
    
    if (this.input) { // Input is always present now
      this.input.addEventListener('keypress', (e) => {
        if (e.key === 'Enter' && !this.uiHandlers.isGenerationInProgress() && 
            !this.uiHandlers.isCooldownActive() && this.input.value.trim()) {
          e.preventDefault();
          this.handleGenerate();
        }
      });
      
      this.input.addEventListener('input', () => {
        if (this.selectedColorCard && this.input.value.trim() !== this.selectedColorCard.name.trim()) {
          // Deselect color card if user types something different from the selected card's name
          const previouslySelected = this.colorCardsContainer.querySelector('.letz-ai__color-card.selected');
          if (previouslySelected) {
            previouslySelected.classList.remove('selected');
          }
          this.selectedColorCard = null;
          console.log('[ImageGenerator] Input changed by typing, deselected color card.');
        }
        this.uiHandlers.updateButtonStatus();
      });
    }
    
    // Scroll event listener
    if (this.gallery) {
      this.gallery.addEventListener('scroll', this.handleScroll.bind(this));
    }
    
    // Prevent form submission when the generate button is clicked
    document.addEventListener('submit', (e) => {
      if (this.uiHandlers.isGenerationInProgress()) {
        console.log('Preventing form submission during image generation');
        e.preventDefault();
        return false;
      }
    });
  }

  renderColorCards() {
    if (!this.colorCardsContainer) {
      console.warn('[ImageGenerator RenderCards] No color cards container found. Skipping render.');
      return;
    }
    
    if (!this.colorCardData || this.colorCardData.length === 0) {
      if (this.colorCardsContainer) this.colorCardsContainer.style.display = 'none'; 
      if (this.colorCardsTitle) this.colorCardsTitle.style.display = 'none';
      console.warn('[ImageGenerator RenderCards] No color card data available (this.colorCardData is empty or null). Hiding container and title.');
      return;
    }
    
    console.log(`[ImageGenerator RenderCards] Starting to render ${this.colorCardData.length} color cards. Data:`, JSON.stringify(this.colorCardData));
    
    if (this.colorCardsTitle) this.colorCardsTitle.style.display = 'block';
    if (this.colorCardsContainer) this.colorCardsContainer.style.display = 'flex';

    this.colorCardsContainer.innerHTML = ''; 

    this.colorCardData.forEach((cardData, index) => {
      console.log(`[ImageGenerator RenderCards #${index}] --- Processing Card for DOM --- Name: "${cardData ? cardData.name : 'N/A'}". Full data:`, JSON.stringify(cardData));
      
      if (!cardData || typeof cardData.name !== 'string' || !cardData.name.trim() || !Array.isArray(cardData.colors) || cardData.colors.length === 0) {
        console.warn(`[ImageGenerator RenderCards #${index}] Invalid cardData or missing essential properties (name/colors). Skipping DOM creation for this card. Data:`, cardData);
        return; 
      }

      const cardElement = document.createElement('div');
      cardElement.className = 'letz-ai__color-card';
      cardElement.setAttribute('role', 'button');
      cardElement.setAttribute('tabindex', '0');

      // Use display_name for accessibility, fallback to name if display_name doesn't exist
      const displayName = cardData.display_name || cardData.name;
      cardElement.setAttribute('aria-label', `Selecionar paleta de cores: ${displayName}`);

      // Use ColorCardUtils if available, otherwise fallback to manual gradient creation
      if (window.ColorCardUtils && window.ColorCardUtils.createGradientCSS && window.ColorCardUtils.applyGradientToCard) {
        console.log(`[ImageGenerator RenderCards #${index}] Using ColorCardUtils for "${cardData.name}"`);
        const gradientCSS = window.ColorCardUtils.createGradientCSS(cardData.colors);
        console.log(`[ImageGenerator RenderCards #${index}] Generated gradient CSS: ${gradientCSS}`);
        window.ColorCardUtils.applyGradientToCard(cardElement, gradientCSS);
      } else {
        console.warn(`[ImageGenerator RenderCards #${index}] ColorCardUtils not available, using fallback for "${cardData.name}"`);
        // Fallback to manual gradient creation
        const numColors = cardData.colors.length;
        let gradientCss = '';

        if (numColors === 1) {
          gradientCss = cardData.colors[0];
        } else if (numColors > 0) {
          const stops = cardData.colors.map((color, i) => {
            const percentage = (i / (numColors - 1)) * 100;
            return `${color} ${percentage}%`;
          }).join(', ');
          gradientCss = `linear-gradient(90deg, ${stops})`;
        } else {
          gradientCss = '#333';
        }
        
        // Apply using background property (not background-image)
        if (gradientCss.startsWith('linear-gradient') || gradientCss.startsWith('radial-gradient')) {
          cardElement.style.background = gradientCss;
        } else {
          cardElement.style.backgroundColor = gradientCss;
        }
      }

      const nameElement = document.createElement('div');
      nameElement.className = 'letz-ai__color-card-name';
      // Use display_name for showing to user, fallback to name if display_name doesn't exist
      nameElement.textContent = displayName;

      cardElement.appendChild(nameElement);

      cardElement.addEventListener('click', () => this.handleColorCardSelect(cardData, cardElement));
      cardElement.addEventListener('keypress', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.handleColorCardSelect(cardData, cardElement);
        }
      });

      this.colorCardsContainer.appendChild(cardElement);
    });
    
    // Apply dynamic height calculation after all cards are rendered
    if (window.ColorCardUtils && window.ColorCardUtils.calculateOptimalCardHeight && window.ColorCardUtils.applyOptimalHeightToCards) {
      console.log('[ImageGenerator RenderCards] Applying dynamic height calculation...');
      // Use display_name for height calculations, fallback to name
      const cardNames = this.colorCardData.map(card => card.display_name || card.name);
      const optimalHeight = window.ColorCardUtils.calculateOptimalCardHeight(cardNames);
      window.ColorCardUtils.applyOptimalHeightToCards(optimalHeight);
      console.log(`[ImageGenerator RenderCards] Dynamic height applied: ${optimalHeight}px`);
    } else {
      console.warn('[ImageGenerator RenderCards] ColorCardUtils height functions not available, using default CSS height');
    }
    
    console.log('[ImageGenerator RenderCards] Finished rendering all color cards.');
  }

  handleColorCardSelect(cardData, cardElement) {
    const currentlySelected = this.colorCardsContainer.querySelector('.letz-ai__color-card.selected');
    if (currentlySelected) {
      currentlySelected.classList.remove('selected');
    }

    cardElement.classList.add('selected');
    this.selectedColorCard = cardData; // cardData contains .name, .colors. It may or may not contain .prompts.
    
    console.log('[ImageGenerator] handleColorCardSelect called with:', cardData);
    console.log('[ImageGenerator] this.input exists:', !!this.input);
    console.log('[ImageGenerator] this.input element:', this.input);
    
    if (this.input) {
      const displayValue = cardData.display_name || cardData.name;
      console.log('[ImageGenerator] Setting input value to:', displayValue);
      
      this.input.value = displayValue;
      console.log('[ImageGenerator] Input value after setting:', this.input.value);
      console.log(`[ImageGenerator] Color card selected: "${cardData.name}" (ID: ${cardData.name}, Display: ${cardData.display_name || cardData.name}). Input field value set to: "${this.input.value}"`);
      this.input.dispatchEvent(new Event('input', { bubbles: true }));
    } else {
      console.warn('[ImageGenerator] Input element not found during color card selection.');
    }

    this.uiHandlers.updateButtonStatus(); 
    console.log('[ImageGenerator] Color card selected (full data):', cardData.name, 'Associated client-side prompts (if any):', cardData.prompts);
  }

  handleScroll(e) {
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    const debounceTime = this.isMobile ? 500 : 250; 
    this.scrollTimeout = setTimeout(() => {
      const container = e.target;
      const currentScrollPos = container.scrollTop || window.scrollY;
      let scrollDirection = 'down';
      if (this.isMobile) {
        const currentHorizontalPos = container.scrollLeft;
        if (this._lastHorizontalPos !== undefined) {
          scrollDirection = currentHorizontalPos > this._lastHorizontalPos ? 'right' : 'left';
        }
        this._lastHorizontalPos = currentHorizontalPos;
        this.lastScrollDirection = scrollDirection === 'right' ? 'down' : 'up';
        const now = Date.now();
        if (now - this.lastLoadTime < this.mobileScrollCooldown) {
          console.log('Mobile scroll cooldown active, ignoring scroll event');
          return; 
        }
      } else {
        if (!this._lastScrollPos) {
          this._lastScrollPos = currentScrollPos;
          return;
        }
        if (currentScrollPos > this._lastScrollPos) {
          this.lastScrollDirection = 'down';
        } else if (currentScrollPos < this._lastScrollPos) {
          this.lastScrollDirection = 'up';
        }
        this._lastScrollPos = currentScrollPos;
      }
      this.lastScrollPosition = currentScrollPos; 
      if (this.lastScrollDirection === 'down') {
        this.checkForPreload(container);
      } else {
        console.log('Scrolling up/left, not checking for preload');
      }
    }, debounceTime);
  }
  
  checkForPreload(container) {
    if (this.eventHandlers.lastScrollDirection !== 'down' || this.loadingMoreImages || !this.hasMoreImages) {
      return;
    }
    
    let scrollPercentage;
    if (this.isMobile) {
      const scrollWidth = container.scrollWidth;
      const scrollLeft = container.scrollLeft;
      const clientWidth = container.clientWidth;
      scrollPercentage = (scrollLeft + clientWidth) / scrollWidth;
      if (scrollPercentage < 0.90) {
        return; 
      }
    } else {
      const scrollHeight = container.scrollHeight;
      const scrollTop = container.scrollTop;
      const clientHeight = container.clientHeight;
      scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
      if (scrollPercentage < this.preloadThreshold) {
        return;
      }
    }
    
    console.log(`Preloading triggered at ${(scrollPercentage * 100).toFixed(1)}% scroll`);
    this.loadMoreImages();
    this.lastLoadTime = Date.now();
  }
  
  setupInfiniteScroll() {
    console.log('Setting up infinite scroll with strict mobile settings');
    if (!this.gallery) {
      console.log('No gallery found, skipping infinite scroll setup');
      return;
    }
    if (!('IntersectionObserver' in window)) {
      console.log('IntersectionObserver not supported in this browser');
      return;
    }
    if (this.intersectionObserver) {
      console.log('Disconnecting existing observer');
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
    const options = {
      root: null, 
      rootMargin: this.isMobile ? '0px 50px 0px 0px' : '0px 0px 300px 0px',
      threshold: this.isMobile ? 0.3 : 0.05
    };
    console.log('Creating new IntersectionObserver with device-specific settings:', options);
    this.intersectionObserver =  new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          console.log('Sentinel element visible in viewport!', entry.intersectionRatio);
          const now = Date.now();
          if (this.isMobile && (now - this.lastLoadTime < this.mobileScrollCooldown)) {
            console.log('Mobile cooldown active, skipping load');
            return;
          }
          if (this.hasMoreImages && !this.loadingMoreImages && this.eventHandlers.lastScrollDirection === 'down') {
            console.log('Loading more images since conditions are met');
            setTimeout(() => {
              if (!this.loadingMoreImages) {
                this.loadMoreImages();
                this.lastLoadTime = now;
              }
            }, 100);
          }
        }
      });
    }, options);
    this.galleryRenderer.updateInfiniteScrollSentinel();
    const sentinel = document.getElementById('infinite-scroll-sentinel');
    if (sentinel) {
      this.intersectionObserver.observe(sentinel);
      console.log('Started observing sentinel element with device-specific settings');
    }
  }

  async loadImagesFromAPI(reset = false) {
    try {
      const scrollPos = this.gallery ? this.gallery.scrollTop : 0;
      const horizontalPos = this.isMobile && this.gallery ? this.gallery.scrollLeft : 0;
      
      if (reset) {
        this.apiService.resetPagination();
        this.page = 1; 
      }
      
      console.log('[ImageGenerator] loadImagesFromAPI called with reset:', reset);
      console.log('[ImageGenerator] Metacampo images available:', this.featuredImagesFromMetacampo.length);
      console.log('[ImageGenerator] Product filter onlyThisProduct:', this.productFilterOnlyThis);
      
      // Pass onlyThisProduct parameter to API service
      const { images, hasMore } = await this.apiService.loadImagesFromAPI({ 
        reset, 
        onlyThisProduct: this.productFilterOnlyThis 
      });
      this.hasMoreImages = hasMore;
      this.galleryRenderer.setHasMoreImages(hasMore);
      
      // Combine metacampo featured images with API images - ADD ONLY TO GENERATED IMAGES
      let combinedImages = [];
      
      if (reset) {
        // On reset, start with metacampo images, then add API images
        combinedImages = [...this.featuredImagesFromMetacampo, ...images];
        this.generatedImages = combinedImages; // ONLY completed images here
        console.log('[ImageGenerator] Reset=true: Combined completed images count:', combinedImages.length, 
          'Metacampo:', this.featuredImagesFromMetacampo.length, 'API:', images.length);
      } else {
        // On pagination, only add new API images
        if (images.length > 0) {
          const existingIds = new Set(this.generatedImages.map(img => 
            typeof img === 'object' ? img.id : null).filter(id => id !== null));
          const newImages = images.filter(img => {
            if (typeof img === 'object' && img.id) {
              return !existingIds.has(img.id);
            }
            return true; 
          });
          
          if (newImages.length > 0) {
            this.generatedImages = [...this.generatedImages, ...newImages]; // ONLY completed images
            this.page++;
          }
          combinedImages = newImages;
        }
      }
      
      if (reset && combinedImages.length > 0) {
        console.log('[ImageGenerator] Rendering gallery with completed images on reset:', combinedImages.length);
        console.log('🚀 [RENDER CALL] From updateGalleryDisplay - RESET with completed images');
        this.galleryRenderer.renderGallery(this.generatedImages, { append: false });
      } else if (!reset && combinedImages.length > 0) {
        console.log('🚀 [RENDER CALL] From updateGalleryDisplay - APPEND combined images');
        this.galleryRenderer.renderGallery(combinedImages, { append: true });
      } else if (reset) {
        console.log('[ImageGenerator] No images to render on reset');
        console.log('🚀 [RENDER CALL] From updateGalleryDisplay - RESET empty');
        this.galleryRenderer.renderGallery([], { append: false });
      }
      
      // Update gallery empty state after rendering
      this.updateGalleryEmptyState();
      
      // Mark images as loaded and check if global loading should be hidden
      this.imagesLoaded = true;
      console.log('[DEBUG LOADING] Images loaded successfully, triggering loading check...');
      
      // Add delay to ensure everything is properly initialized
      setTimeout(() => {
        this.checkAndHideGlobalLoading();
      }, 200);
      
        // Verificar se há parâmetro source na URL
        const urlParams = new URLSearchParams(window.location.search);
        const hasSourceParameter = urlParams.has('source');
        
        if (!hasSourceParameter && this.generatedImages.length > 0 && !this.selectedImage) {
            // Definir primeira imagem como selecionada
            this.selectedImage = this.generatedImages[0];
            // Aplicar visualmente a seleção da primeira imagem
            if (this.selectedImage && this.selectedImage.image_url) {
              this.galleryRenderer.updateSelectedState(this.selectedImage.image_url);
            }
        } else if (hasSourceParameter) {
            console.log('[ImageGenerator] Source parameter detected, skipping auto-selection in loadImagesFromAPI');
        }
        if (this.galleryHeader) {
          this.galleryHeader.classList.remove('hidden');
        }
      
      if (!reset && this.gallery) {
        setTimeout(() => {
          if (this.isMobile) {
            this.gallery.scrollLeft = horizontalPos;
          } else {
            this.gallery.scrollTop = scrollPos;
          }
        }, 50);
      }
      
      if (hasMore && !reset && !this.loadingMoreImages && this.page <= 2 && !this.isMobile) {
        setTimeout(() => {
          this.setupInfiniteScroll();
        }, 500);
      }
      
      setTimeout(() => {
        this.setupInfiniteScroll();
      }, 200);
      
      return { images: this.generatedImages, hasMore };
    } catch (error) {
      console.error('Error in loadImagesFromAPI:', error);
      this.loadingMoreImages = false;
      
      // Mark as loaded even on error to prevent infinite loading
      this.imagesLoaded = true;
      console.log('[DEBUG LOADING] Error loading images, but marking as loaded, triggering loading check...');
      setTimeout(() => {
        this.checkAndHideGlobalLoading();
      }, 200);
      
      return { images: [], hasMore: false };
    }
  }

  async loadMoreImages() {
    if (this.loadingMoreImages || !this.hasMoreImages) {
      return;
    }
    
    const scrollPos = this.gallery ? this.gallery.scrollTop : 0;
    const horizontalPos = this.isMobile && this.gallery ? this.gallery.scrollLeft : 0;
    
    this.loadingMoreImages = true;
    try {
      const { images, hasMore } = await this.loadImagesFromAPI(false);
      this.hasMoreImages = hasMore;
      this.galleryRenderer.setHasMoreImages(hasMore);
      
      if (this.gallery) {
        setTimeout(() => {
          if (this.isMobile) {
            this.gallery.scrollLeft = horizontalPos;
          } else {
            this.gallery.scrollTop = scrollPos;
          }
        }, 100);
      }
      
      setTimeout(() => {
        this.setupInfiniteScroll();
      }, 300);
      
      this.lastLoadTime = Date.now();
      return { images, hasMore };
    } catch (error) {
      console.error('Error loading more images:', error);
      this.uiHandlers.showToast(window.t('toasts.load_more_images_error'), 'error');
      return { images: [], hasMore: false };
    } finally {
      setTimeout(() => {
        this.loadingMoreImages = false;
      }, 200);
    }
  }

  async loadInitialImages(productOnly = false) {
    console.log('Loading initial images, productOnly:', productOnly);
    
    try {
      this.uiHandlers.showGalleryLoading();
      
      const options = {
        reset: true,
        onlyThisProduct: productOnly
      };

      const response = await this.apiService.loadImagesFromAPI(options);
      
      if (response.images && response.images.length > 0) {
        console.log(`Loaded ${response.images.length} initial images`);
        
        this.galleryRenderer.setHasMoreImages(response.has_more || false);
        console.log('🚀 [RENDER CALL] From loadInitialImages - RESET with initial images');
        this.galleryRenderer.renderGallery(response.images, { append: false });
        
        // Always update empty state after initial load
        setTimeout(() => {
          this.updateGalleryEmptyState();
        }, 50);
        
      } else {
        console.warn('No images loaded or API error');
        console.log('🚀 [RENDER CALL] From loadInitialImages - RESET empty (no images loaded)');
        this.galleryRenderer.renderGallery([], { append: false });
        this.updateGalleryEmptyState();
      }
      
    } catch (error) {
      console.error('Error loading initial images:', error);
      console.log('🚀 [RENDER CALL] From loadInitialImages - RESET empty (error occurred)');
      this.galleryRenderer.renderGallery([], { append: false });
      this.updateGalleryEmptyState();
    } finally {
      this.uiHandlers.hideGalleryLoading();
    }
  }

  addImageToGallery(imageData) {
    console.log('[ImageGenerator] ✅ FINAL STEP: Adding COMPLETED image to main gallery:', imageData);

    if (!imageData) {
      console.warn('[ImageGenerator] ❌ No image data provided to addImageToGallery');
      return;
    }

    // STEP 1: Remove from pending generations (if exists)
    if (this.pendingGenerations.length > 0) {
      const pendingIndex = this.pendingGenerations.findIndex(pending => 
        pending.generationId === imageData.generationId
      );
      
      if (pendingIndex !== -1) {
        console.log('[ImageGenerator] ✅ Removing from pending generations:', this.pendingGenerations[pendingIndex]);
        this.pendingGenerations.splice(pendingIndex, 1);
      } else {
        console.warn('[ImageGenerator] ⚠️ Image not found in pending generations, but continuing...');
      }
    }

    // STEP 2: Add ONLY to completed images array
    this.generatedImages.unshift(imageData);
    console.log('[ImageGenerator] ✅ Image added to COMPLETED images ONLY. Total completed:', this.generatedImages.length);
    console.log('[ImageGenerator] 📊 Pending generations remaining:', this.pendingGenerations.length);

    // STEP 3: Replace placeholder in gallery
    const success = this.galleryRenderer.replacePlaceholderWithImage(imageData);
    
    if (success) {
      console.log('[ImageGenerator] ✅ Gallery placeholder successfully replaced');
      
      // Set as selected image if no image is currently selected
        if (!this.selectedImage) {
            // Definir nova imagem gerada como selecionada
            this.selectedImage = imageData;
            // Aplicar visualmente a seleção da nova imagem gerada
            if (this.selectedImage && this.selectedImage.image_url) {
              this.galleryRenderer.updateSelectedState(this.selectedImage.image_url);
            }
        console.log('[ImageGenerator] ✅ Set as selected image');
      }

      // Show gallery header if it was hidden
      if (this.galleryHeader) {
        this.galleryHeader.classList.remove('hidden');
      }
    } else {
      console.warn('[ImageGenerator] ⚠️ Failed to replace placeholder, falling back to full gallery render');
      // Fallback: render the entire gallery if placeholder replacement failed
      console.log('🚀 [RENDER CALL] From addGeneratedImage - FALLBACK full render');
      this.galleryRenderer.renderGallery(this.generatedImages, { append: false });
    }

    // Update gallery empty state after adding image
    this.updateGalleryEmptyState();

    console.log('[ImageGenerator] ✅ addImageToGallery completed successfully');
  }

  async handleGenerate() {
    console.log(`[ImageGenerator] 🚀 Generate button clicked. ProductType: "${this.productType}"`);
    
    // Check if user is logged in first
    if (!this.customerId) {
      this.redirectToShopifyLogin();
      return;
    }

    // STEP 1: Acquire generation lock
    if (this.generationLock) {
      console.log('[ImageGenerator] ⏳ Generation lock active, ignoring request');
      return;
    }

    this.generationLock = true;
    console.log('[ImageGenerator] 🔒 Generation lock acquired');

    try {
      // STEP 2: Set generation state atomically
      this.isGenerating = true;
      this.generatingImageId = `gen_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      console.log('[ImageGenerator] 🎯 Starting generation with unique ID:', this.generatingImageId);

      let apiPayload;
      const currentInputValue = this.input ? this.input.value.trim() : ""; 
      const selectedColorCard = this.colorCardManager.getSelectedColorCard();

      if (selectedColorCard && selectedColorCard.name) {
        // When a color card is selected, determine the correct type based on product type
        if (this.productType === 'color_card') {
          apiPayload = {
            type: 'color_card',
            colorName: selectedColorCard.name
          };
        } else if (this.productType === 'image_card_with_upload') {
          apiPayload = {
            type: 'image_card_with_upload',
            colorName: selectedColorCard.name
          };
          
          // Include uploaded image if available
          const uploadedImage = this.getUploadedImage();
          if (uploadedImage) {
            apiPayload.inputImage = uploadedImage;
            console.log('[ImageGenerator] Including uploaded image in request');
          } else {
            console.log('[ImageGenerator] No uploaded image found for image_card_with_upload');
          }
        } else {
          // For other product types, use the actual product type or default to color_card
          apiPayload = {
            type: this.productType || 'color_card',
            colorName: selectedColorCard.name
          };
        }
      } else { 
        const colorCardRequiredProductTypes = ['color_card']; 
        if (colorCardRequiredProductTypes.includes(this.productType) && !selectedColorCard) {
          this.uiHandlers.showToast(window.t('toasts.select_palette'), 'error');
          return;
        }
        
        if (!currentInputValue) {
          this.uiHandlers.showToast(window.t('toasts.enter_item'), 'error');
          return;
        }
        
        // Use the actual product type or default to "default"
        apiPayload = {
          type: this.productType || "default", 
          prompt: currentInputValue 
        };
      }

      console.log(`[ImageGenerator] 📋 API payload prepared:`, apiPayload);

      // STEP 3: Create pending generation entry - KEEP SEPARATE FROM GENERATED IMAGES
      const pendingImage = {
        id: `pending_${this.generatingImageId}`,
        generationId: this.generatingImageId,
        status: 'generating',
        isPending: true,
        timestamp: Date.now()
      };

      // ADD ONLY TO PENDING - NOT TO GENERATED IMAGES
      this.pendingGenerations.push(pendingImage);
      console.log('[ImageGenerator] 📝 Added to PENDING ONLY (not to generated images):', pendingImage);
      console.log('[ImageGenerator] 📊 Current state - Pending:', this.pendingGenerations.length, 'Completed:', this.generatedImages.length);

      // STEP 4: Start UI feedback
      this.uiHandlers.setGenerating(true);
      
      this.generationTimeout = setTimeout(() => {
        console.error('[ImageGenerator] ⏰ Generation timeout reached');
        this.uiHandlers.forceResetState();
        this.galleryRenderer.removePlaceholder();
        this.resetGenerationState();
      }, 120000); // CHANGED: Increased timeout from 30000 to 120000 (120 seconds)
      
      // STEP 5: Add placeholder to gallery with uploaded image if available
      console.log('[ImageGenerator] 🔄 Adding protected placeholder to gallery...');
      
      // Get uploaded image for blur effect if available (selectedColorCard already declared above)
      const uploadedImageBase64 = this.uiHandlers.getUploadedFileBase64();
      
      this.galleryRenderer.addPlaceholderToGallery(uploadedImageBase64, selectedColorCard, this.productType);
      
      // STEP 6: Generate image
      console.log('[ImageGenerator] 🎨 Starting actual image generation...');
      const imageObject = await this.apiService.generateImage(JSON.stringify(apiPayload));
      
      // STEP 7: Add generation ID to track this specific image
      imageObject.generationId = this.generatingImageId;
      console.log('[ImageGenerator] ✅ Image generation completed:', imageObject.id);
      
      // STEP 8: Move from pending to completed - THIS IS THE KEY CHANGE
      console.log('[ImageGenerator] 🔄 Moving image from pending to completed arrays...');
      this.addImageToGallery(imageObject); // This will handle the array transfer
      await this.customilyIntegration.uploadToCustomily(imageObject.image_url);
      
      // STEP 9: Success feedback
      this.uiHandlers.showToast(window.t('toasts.generated_success'), 'success');
      this.uiHandlers.startCountdown();
      
      console.log('[ImageGenerator] 🎉 Generation process completed successfully');
      console.log('[ImageGenerator] 📊 Final state - Pending:', this.pendingGenerations.length, 'Completed:', this.generatedImages.length);
      
    } catch (error) {
      console.error('[ImageGenerator] ❌ Error in handleGenerate:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      if (errorMessage.includes('expirada') || errorMessage.includes('login')) {
        this.uiHandlers.showToast(window.t('toasts.session_expired'), 'error');
      } else {
        this.uiHandlers.showToast(window.t('toasts.generate_image_error', {error: errorMessage}), 'error');
      }
    } finally {
      // STEP 10: Always clean up state
      this.uiHandlers.setGenerating(false);
      if (this.generationTimeout) {
        clearTimeout(this.generationTimeout);
        this.generationTimeout = null;
      }
      this.galleryRenderer.removePlaceholder();
      this.resetGenerationState();
      console.log('[ImageGenerator] 🧹 Generation cleanup completed');
    }
  }

  resetGenerationState() {
    console.log('[ImageGenerator] 🔄 Resetting generation state');
    
    this.isGenerating = false;
    this.generatingImageId = null;
    this.generationLock = false;
    
    // Clean up any orphaned pending generations
    const orphanedCount = this.pendingGenerations.length;
    if (orphanedCount > 0) {
      console.warn(`[ImageGenerator] 🧹 Cleaning up ${orphanedCount} orphaned pending generations`);
      this.pendingGenerations = [];
    }
    
    console.log('[ImageGenerator] ✅ Generation state reset complete');
  }

  setGenerating(isGenerating) {
    console.log(`[ImageGenerator] 🔄 Setting generating state: ${isGenerating}`);
    this.isGenerating = isGenerating;
    
    // Get the wrapper element for animation control
    const wrapper = document.querySelector('[data-ai-wrapper]');
    
    // Check if there's an active scanner (uploaded image with scanner effect)
    const hasActiveScanner = wrapper && wrapper.querySelector('.scanframe .scanner-bar');
    
    // Control AI thinking animation on wrapper - BUT NOT if scanner is active
    if (wrapper) {
      if (isGenerating && !hasActiveScanner) {
        wrapper.classList.add('letz-ai--generating');
        console.log('[ImageGenerator] ✨ AI thinking animation started');
      } else {
        wrapper.classList.remove('letz-ai--generating');
        console.log('[ImageGenerator] 🎯 AI thinking animation stopped (scanner active or not generating)');
      }
    }
    
    // Update button state
    const button = this.container.querySelector('[data-generate-button]');
    if (button) {
      button.disabled = isGenerating;
      
      const buttonText = button.querySelector('[data-button-text]');
      const buttonLoader = button.querySelector('[data-btn-loader]');
      
      if (buttonText && buttonLoader) {
        if (isGenerating) {
          buttonText.classList.add('hidden');
          buttonLoader.classList.remove('hidden');
          button.classList.add('has-content');
        } else {
          buttonText.classList.remove('hidden');
          buttonLoader.classList.add('hidden');
          button.classList.remove('has-content');
        }
      }
    }
    
    // Update global loading overlay
    const globalLoading = this.container.querySelector('[data-global-loading]');
    if (globalLoading) {
      if (isGenerating) {
        globalLoading.classList.remove('hidden');
      } else {
        globalLoading.classList.add('hidden');
      }
    }
  }

  redirectToShopifyLogin() {
    try {
      // Get current input text
      const currentInputText = this.input ? this.input.value.trim() : '';
      
      // Get selected card data (for card-based products)
      let selectedCard = null;
      if ((this.productType === 'color_card' || this.productType === 'image_card' || this.productType === 'image_card_with_upload') && this.selectedColorCard) {
        selectedCard = {
          name: this.selectedColorCard.name,
          colors: this.selectedColorCard.colors,
          productType: this.productType
        };
      }
      
      // Get uploaded image data (for image_card_with_upload)
      let uploadedImageData = null;
      if (this.productType === 'image_card_with_upload' && this.uiHandlers && this.uiHandlers.uploadedFileBase64) {
        uploadedImageData = {
          base64: this.uiHandlers.uploadedFileBase64,
          fileName: this.uiHandlers.uploadedFileName || 'uploaded-image.jpg'
        };
      }
      
      // Save complete state using LocalStorageManager
      this.localStorageManager.saveStateForLogin(currentInputText, selectedCard, uploadedImageData);
      
      // Show informative toast before redirect
      this.uiHandlers.showToast(window.t('toasts.redirecting_login'), 'info');
      
      // Small delay to show the toast, then redirect
      setTimeout(() => {
        // Build Shopify login URL with return parameter
        const currentUrl = window.location.href;
        const loginUrl = `/account/login?return_url=${encodeURIComponent(currentUrl)}`;
        console.log('[ImageGenerator] Redirecting to Shopify login:', loginUrl);
        window.location.href = loginUrl;
      }, 1000);
      
    } catch (error) {
      console.error('[ImageGenerator] Error redirecting to login:', error);
      // Fallback to showing toast message if redirect fails
      this.uiHandlers.showToast(window.t('toasts.login_required'), 'error');
    }
  }

  handleImageClick(imageData) {
    const imageUrl = typeof imageData === 'string' ? imageData : imageData.image_url;
    console.log('Image clicked:', imageUrl);
    this.galleryRenderer.updateSelectedState(imageUrl);
    this.selectedImage = imageData;
    
    // Removed the forced re-render for mobile to prevent unnecessary reloading of all images
    // The updateSelectedState method already handles the visual selection update
    
    this.customilyIntegration.uploadToCustomily(imageUrl)
      .catch(error => {
        this.uiHandlers.showToast(window.t('toasts.apply_art_error', {error: error.message}), 'error');
      });
  }

  copyPromptToClipboard(prompt) {
    if (!prompt) return;
    try {
      navigator.clipboard.writeText(prompt).then(() => {
        // Handle input filling differently based on product type
        if (this.input) {
          if (this.productType === 'color_card' || this.productType === 'image_card_with_upload') {
            // Find and select the corresponding color card first
            const matchingCard = this.colorCardData.find(card => 
              prompt.toLowerCase().includes(card.name.toLowerCase()) || 
              card.name.toLowerCase().includes(prompt.toLowerCase())
            );
            
            console.log(`[ImageGenerator] Debug - Prompt: "${prompt}"`);
            console.log(`[ImageGenerator] Debug - Available cards:`, this.colorCardData.map(c => c.name));
            console.log(`[ImageGenerator] Debug - Matching card found:`, matchingCard);
            
            // Validate that we found a matching card
            if (!matchingCard) {
              this.uiHandlers.showToast(window.t('letz_ai.toasts.image_not_from_product'), 'warning');
              console.log(`[ImageGenerator] No card found for prompt: "${prompt}"`);
              return;
            }
            
            // Use display_name for input if card is found, otherwise use prompt
            const inputValue = matchingCard ? (matchingCard.display_name || matchingCard.name) : prompt;
            this.colorCardManager.setInputValueAndLock(inputValue);
            
            // Show input field if it was hidden
            const inputContainer = this.colorCardManager.getInputFieldContainer();
            if (inputContainer) {
              inputContainer.classList.remove('letz-ai__input-hidden');
              this.colorCardManager.inputShownOnce = true; // Mark as shown once
              console.log(`[ImageGenerator] Input shown and locked after copy prompt for ${this.productType}`);
            }
            
            // Select the card using ColorCardManager's method for proper state management
            if (matchingCard) {
              console.log(`[ImageGenerator] About to select card: "${matchingCard.name}"`);
              this.colorCardManager.selectCardByName(matchingCard.name);
              console.log(`[ImageGenerator] Color card auto-selected after copy prompt: "${matchingCard.name}"`);
              
              // Additional debug - check if selection worked
              const selectedCard = this.colorCardManager.getSelectedColorCard();
              console.log(`[ImageGenerator] Debug - Selection result:`, selectedCard);
            } else {
              // Clear any existing selection since no matching card was found
              this.colorCardManager.deselectColorCard();
              console.log(`[ImageGenerator] No matching card found for prompt: "${prompt}"`);
            }
          } else {
            // For other product types (like image_card), use normal flow
            this.input.value = prompt;
            this.input.dispatchEvent(new Event('input', { bubbles: true }));
            this.colorCardManager.updateFieldContentClass(); // Update has-content class
            this.input.focus();
          }
          
          this.uiHandlers.updateButtonStatus();
        }
        this.uiHandlers.showToast(window.t('toasts.prompt_copied'), 'success');
      });
    } catch (error) {
      console.error('Failed to copy prompt:', error);
      this.uiHandlers.showToast(window.t('toasts.prompt_copy_error'), 'error');
    }
  }

  async removeFromGallery(imageData, buttonElement) {
    console.log('[ImageGenerator] 🗑️ ULTRA-PROTECTED removeFromGallery called');
    console.log('[ImageGenerator] 🔍 DETAILED ANALYSIS of removal request:', {
      imageData: typeof imageData === 'object' ? { 
        id: imageData.id, 
        generationId: imageData.generationId,
        isPending: imageData.isPending,
        status: imageData.status
      } : imageData,
      buttonElement: buttonElement ? {
        className: buttonElement.className,
        parentClassName: buttonElement.parentElement?.className,
        hasGeneratingAttribute: buttonElement.hasAttribute('data-generating'),
        hasPlaceholderClass: buttonElement.classList.contains('generating-placeholder')
      } : 'null',
      currentGenerationState: {
        isGenerating: this.isGenerating,
        generatingImageId: this.generatingImageId,
        pendingCount: this.pendingGenerations.length,
        completedCount: this.generatedImages.length
      }
    });

    try {
      const imageId = typeof imageData === 'string' ? null : imageData.id;
      const imageUrl = typeof imageData === 'string' ? imageData : imageData.image_url;
      const isFeatured = typeof imageData === 'object' && imageData.is_featured === true;
      const isFromMetacampo = typeof imageData === 'object' && imageData.from_metacampo === true;
      const generationId = typeof imageData === 'object' ? imageData.generationId : null;
      const isPendingImage = typeof imageData === 'object' && imageData.isPending === true;

      // 🛡️ PROTECTION 1: Block featured images
      if (isFeatured) {
        console.log('[ImageGenerator] ❌ BLOCKED: Featured image cannot be removed');
        this.uiHandlers.showToast(window.t('toasts.featured_images_protected'), 'error');
        return;
      }

      // 🛡️ PROTECTION 2: Block if this is clearly a pending generation
      if (isPendingImage) {
        console.log('[ImageGenerator] ❌ BLOCKED: Attempt to delete pending generation image');
        this.uiHandlers.showToast(window.t('toasts.wait_generation_finish'), 'warning');
        return;
      }

      // 🛡️ PROTECTION 3: Block if currently generating AND this matches the generating image
      if (this.isGenerating && generationId === this.generatingImageId) {
        console.log('[ImageGenerator] ❌ BLOCKED: Attempt to delete currently generating image');
        console.log('[ImageGenerator] Generation match details:', {
          generatingImageId: this.generatingImageId,
          requestedGenerationId: generationId,
          exactMatch: generationId === this.generatingImageId
        });
        this.uiHandlers.showToast(window.t('toasts.wait_generation_finish'), 'warning');
        return;
      }

      // 🛡️ PROTECTION 4: Block if this is in pending generations array
      const isPendingGeneration = this.pendingGenerations.some(pending => 
        pending.generationId === generationId || 
        pending.id === imageId ||
        (imageId && imageId.includes('pending'))
      );
      
      if (isPendingGeneration) {
        console.log('[ImageGenerator] ❌ BLOCKED: Image found in pending generations array');
        this.uiHandlers.showToast(window.t('toasts.wait_generation_finish'), 'warning');
        return;
      }
      
      // 🛡️ PROTECTION 5: Block generation-related IDs
      if (imageId && (imageId.includes('generating') || imageId.includes('pending'))) {
        console.log('[ImageGenerator] ❌ BLOCKED: Image ID indicates generation in progress');
        this.uiHandlers.showToast(window.t('toasts.wait_generation_finish'), 'warning');
        return;
      }

      // 🛡️ PROTECTION 6: DOM-level placeholder protection
      if (buttonElement) {
        const parentCard = buttonElement.closest('[data-image-card]');
        if (parentCard) {
          const isPlaceholder = parentCard.classList.contains('generating-placeholder') ||
                               parentCard.hasAttribute('data-generating') ||
                               parentCard.classList.contains('letz-ai__gallery-item--generating');
          
          if (isPlaceholder) {
            console.log('[ImageGenerator] ❌ BLOCKED: DOM element is a placeholder');
            this.uiHandlers.showToast(window.t('toasts.wait_generation_finish'), 'warning');
            return;
          }
        }
      }

      // 🛡️ PROTECTION 7: Final safety check for any generation-related activity
      if (this.isGenerating) {
        console.log('[ImageGenerator] ❌ BLOCKED: System is currently generating, blocking all deletions as safety measure');
        this.uiHandlers.showToast(window.t('toasts.wait_generation_finish_any'), 'warning');
        return;
      }

      console.log('[ImageGenerator] ✅ ALL PROTECTION CHECKS PASSED - proceeding with safe deletion');
      
      if (buttonElement) {
        buttonElement.classList.add('is-loading');
        buttonElement.disabled = true;
      }
      
      // Only try to delete from API if it's not from metacampo and has a valid ID
      if (imageId && !isFromMetacampo) {
        console.log('[ImageGenerator] 🌐 Deleting from API:', imageId);
        await this.apiService.deleteImage(imageData);
      } else {
        console.log('[ImageGenerator] ⏭️ Skipping API deletion (metacampo or no ID)');
      }
      
      const initialLength = this.generatedImages.length;
      console.log('[ImageGenerator] 📊 Initial COMPLETED images array length:', initialLength);

      // ULTRA-SPECIFIC MATCHING: Filter ONLY the completed images array
      this.generatedImages = this.generatedImages.filter((img, index) => {
        // Primary criterion: exact object reference match
        if (img === imageData) {
          console.log('[ImageGenerator] 🎯 Removing by exact object reference at index:', index);
          return false;
        }
        
        // Secondary criterion: ID match (only if IDs exist and are not generation-related)
        if (imageId && img && typeof img === 'object' && img.id === imageId) {
          // Extra safety: don't remove if this looks like a generation ID
          if (img.id.includes('generating') || img.id.includes('pending')) {
            console.log('[ImageGenerator] ⚠️ Skipping removal of generation-related ID:', img.id);
            return true; // Keep this image
          }
          console.log('[ImageGenerator] 🎯 Removing by ID match at index:', index, 'ID:', img.id);
          return false;
        }
        
        // Tertiary criterion: URL match (only for string images or as last resort)
        if (typeof img === 'string' && img === imageUrl) {
          console.log('[ImageGenerator] 🎯 Removing string image by URL match at index:', index);
          return false;
        }
        
        return true; // Keep this image
      });

      const finalLength = this.generatedImages.length;
      console.log('[ImageGenerator] 📊 Final COMPLETED images array length:', finalLength, 'Removed:', initialLength - finalLength, 'images');
      console.log('[ImageGenerator] 📊 Pending generations remain COMPLETELY UNTOUCHED:', this.pendingGenerations.length);

      // Update selected image if we removed it
      if (this.selectedImage === imageData || 
         (typeof this.selectedImage === 'object' && this.selectedImage && this.selectedImage.id === imageId)) {
        // Aplicar visualmente a seleção da nova primeira imagem
        if (this.selectedImage && this.selectedImage.image_url) {
          this.galleryRenderer.updateSelectedState(this.selectedImage.image_url);
        }
        console.log('[ImageGenerator] 🔄 Updated selected image:', this.selectedImage?.id || 'none');
      }
      
      this.renderGallery({ append: false });
      this.uiHandlers.showToast(window.t('toasts.art_removed_success'), 'success');
      await this.loadMoreImagesIfNeeded();
      
      console.log('[ImageGenerator] ✅ ULTRA-PROTECTED removeFromGallery completed successfully');
    } catch (error) {
      console.error('[ImageGenerator] ❌ Error removing image:', error);
      this.uiHandlers.showToast(window.t('toasts.remove_art_error', {error: error.message}), 'error');
    } finally {
      if (buttonElement) {
        buttonElement.classList.remove('is-loading');
        buttonElement.disabled = false;
      }
    }
  }
  
  async loadMoreImagesIfNeeded() {
    if (this.generatedImages.length < 20 && this.hasMoreImages) {
      await this.loadMoreImages();
    }
  }
  
  renderGallery(options = {}) {
    const renderOptions = { ...options, append: options.append === true };
    console.log('🚀 [RENDER CALL] From renderGallery method - options:', renderOptions);
    this.galleryRenderer.renderGallery(this.generatedImages, renderOptions);
    
    // Update gallery empty state after rendering
    this.updateGalleryEmptyState();
  }
  
  isImagesLoaded() {
    console.log('[DEBUG LOADING] isImagesLoaded called, returning:', this.imagesLoaded);
    return this.imagesLoaded;
  }

  checkAndHideGlobalLoading() {
    console.log('[DEBUG LOADING] checkAndHideGlobalLoading called with state:', {
      productType: this.productType,
      imagesLoaded: this.imagesLoaded
    });
    
    // Simplified logic: hide loading as soon as images are loaded for ALL product types
    if (this.imagesLoaded) {
      this.colorCardManager.hideGlobalLoading();
      console.log('[DEBUG LOADING] ✅ Global loading hidden - images are loaded');
    } else {
      console.log('[DEBUG LOADING] ❌ Loading remains visible - images not loaded yet');
    }
  }

  isUserLoggedIn() {
    const isLoggedIn = this.customerId && this.customerId.trim() !== '';
    console.log('[ImageGenerator] Login status check - customerId:', this.customerId, 'isLoggedIn:', isLoggedIn);
    return isLoggedIn;
  }

  getUploadedImage() {
    console.log('getUploadedImage called');
    
    // Check UIHandlers first (for integrated upload)
    if (this.uiHandlers) {
      const base64 = this.uiHandlers.getUploadedFileBase64();
      
      if (base64) {
        console.log('Found uploaded image base64 from UIHandlers');
        return base64; // FIXED: Return only the base64 string
      }
    }
    
    // Fallback to old approach (check for img element with base64 src)
    const imgElement = document.querySelector('[data-integrated-preview-img]');
    if (imgElement && imgElement.src && imgElement.src.startsWith('data:')) {
      console.log('Found uploaded image from preview img element');
      return imgElement.src; // FIXED: Return only the base64 string
    }
    
    console.log('No uploaded image found');
    return null;
  }

  convertImageToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }
}

// Initialize the fingerprinting functionality
function initializeFingerprinting() {
  // Check if OverpoweredJS is available
  if (window.opjs) {
    window.opjs({ API_KEY: 'public_1WX1jR7p8o7kAlmb-WqrrKH-ByydlWM5KcT' })
      .then(fp => {
        console.log('Fingerprint initialized:', fp.clusterUUID);
        window.userFingerprint = fp.clusterUUID;
        
        // Initialize the image generator after fingerprint is ready
        if (!window.imageGenerator) {
          window.imageGenerator = new ImageGenerator();
        } else {
          // Update fingerprint on existing instance
          window.imageGenerator.fingerprint = fp.clusterUUID;
          if (window.imageGenerator.apiService) {
            window.imageGenerator.apiService.fingerprint = fp.clusterUUID;
          }
        }
      })
      .catch(err => {
        console.error('Error initializing fingerprint, retrying in 3 seconds:', err);
        setTimeout(initializeFingerprinting, 3000);
      });
  } else {
    console.error('OverpoweredJS not available, retrying in 3 seconds');
    setTimeout(initializeFingerprinting, 3000);
  }
}

function loadOverpoweredJS() {
  if (window.opjs) {
    return Promise.resolve();
  }
  
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = "https://cdn.overpoweredjs.com/loader/opjs";
    script.async = true;
    script.onload = function() {
      console.log('OverpoweredJS script loaded successfully');
      resolve();
    };
    script.onerror = function(e) {
      console.error('Error loading OverpoweredJS script:', e);
      reject(e);
    };
    document.head.appendChild(script);
  });
}

document.addEventListener('DOMContentLoaded', function() {
  console.log('DOM loaded, initializing fingerprinting');
  
  loadOverpoweredJS()
    .then(() => {
      initializeFingerprinting();
    })
    .catch(err => {
      console.error('Failed to load OverpoweredJS:', err);
      if (!window.imageGenerator) {
        console.warn('Fallback: Initializing ImageGenerator without fingerprinting');
        window.imageGenerator = new ImageGenerator();
      }
    });
  
  setTimeout(function() {
    if (!window.imageGenerator) {
      console.warn('Fallback: Initializing ImageGenerator without fingerprinting after timeout');
      window.imageGenerator = new ImageGenerator();
    }
  }, 5000); 
});

// Add the URL parameter image loading method to ImageGenerator
ImageGenerator.prototype.handleUrlParameterImageLoad = async function() {
  try {
    // Skip if user already has uploaded image in localStorage
    const savedImageData = this.localStorageManager.load('uploadedImageData');
    if (savedImageData) {
      console.log('[ImageGenerator] Skipping URL parameter load - existing image in localStorage');
      return;
    }
    
    // Check for source parameter
    const sourceParam = window.UrlParameterHandler.getSourceParameter();
    if (!sourceParam) {
      console.log('[ImageGenerator] No source parameter found in URL');
      return;
    }
    
    const imageUrl = window.UrlParameterHandler.buildImageUrl(sourceParam);
    if (!imageUrl) {
      console.warn('[ImageGenerator] Could not build image URL from source parameter:', sourceParam);
      return;
    }
    
    console.log('[ImageGenerator] Attempting to load image from URL parameter:', imageUrl);
    
    // Validate image exists
    const imageExists = await window.UrlParameterHandler.validateImageExists(imageUrl);
    if (!imageExists) {
      console.warn('[ImageGenerator] Image from URL parameter does not exist:', imageUrl);
      return;
    }
    
    // Ensure elements are initialized before loading
    this.uiHandlers.initializeIntegratedUpload();
    
    // Load image using UIHandlers
    const success = await this.uiHandlers.loadImageFromUrl(imageUrl, `${sourceParam}.jpg`);
    if (success) {
      console.log('[ImageGenerator] Image loaded successfully from URL parameter');
      
      // Also upload to Customily using original URL
      try {
        console.log('[ImageGenerator] Uploading URL parameter image to Customily...');
        await this.customilyIntegration.uploadToCustomily(imageUrl, false); // false = don't open modal
        console.log('[ImageGenerator] ✅ Image successfully uploaded to Customily');
      } catch (error) {
        console.error('[ImageGenerator] ❌ Error uploading to Customily:', error);
        // Don't fail the whole process, just log the error
      }
      
      this.uiHandlers.showToast(window.t('toasts.image_auto_loaded'), 'success');
    }
  } catch (error) {
    console.error('[ImageGenerator] Error handling URL parameter image load:', error);
  }
};

// Export the class globally
window.ImageGenerator = ImageGenerator;
